import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Grid,
  TextField,
  Box,
  List,
  ListItem,
  Typography,
  Paper,
  InputBase,
  IconButton,
  Menu,
  MenuItem,
  ListItemText
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import UndoIcon from '@material-ui/icons/Undo';
import StopIcon from '@material-ui/icons/Stop';
import { Serializer } from 'survey-core';
import PropTypes from 'prop-types';
import { getEnvVar } from "helpers";
import schema from '../data/schemaMessage.json';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowUpward, Add } from '@material-ui/icons';
import CloseIcon from 'components/CloseIcon';

const useStyles = makeStyles(theme => ({
  aiChatBox: {
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -1px 4px 0 rgba(0,0,0,0.1)',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
    zIndex: 1
  },
  aiChatInputPaper: {
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
    borderRadius: '6px',
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.black,
    border: `0.5px solid`,
    margin: theme.spacing(0.5),
  },
  aiScopeLabel: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
    display: 'inline-flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    border: `1px dashed ${theme.palette.ai.button.secondary}`,
    maxWidth: 'fit-content'
  },
  aiChatInput: {
    flex: 1,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    fontSize: '0.875rem',
    color: theme.palette.text.primary
  },
  aiSendButton: {
    background: theme.palette.ai.button.background,
    padding: '6px',
    borderRadius: '6px',
    color: theme.palette.primary.contrastText,
    width: '32px',
    height: '32px',
    '&:hover': {
      background: theme.palette.ai.button.hoverBackground,
      opacity: 0.9
    },
    '&:disabled': {
      background: theme.palette.action.disabledBackground,
      color: theme.palette.action.disabled
    }
  },
  aiStopButton: {
    background: theme.palette.ai.button.secondary,
    padding: '6px',
    borderRadius: '6px',
    color: theme.palette.button.secondary,
    width: '32px',
    height: '32px',
    '&:hover': {
      background: theme.palette.ai.button.secondary,
      opacity: 0.9
    }
  },
  avatarBox: {
    borderRadius: '100%',
    backgroundColor: '#e9f3fb',
    width: 'fit-content',
    margin: '8px 0px',
  },
  typingLoader: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 12px',
    lineHeight: 0,
    '& span': {
      display: 'inline-block',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: theme.palette.ai.button.background,
      animation: '$bounce 0.6s infinite alternate',
    },
    '& span:nth-child(2)': {
      animationDelay: '0.2s',
    },
    '& span:nth-child(3)': {
      animationDelay: '0.4s',
    },
  },
  '@keyframes bounce': {
    to: {
      transform: 'translateY(-6px)',
    },
  },
  aiButtonBox: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    gap: theme.spacing(1)
  }
}));

const SurveyAIGenerator = ({
  onSuccess,
  onError,
  parseJson = false,
  contextData = null,
  selectedElement = null,
  requestAi = null,
  canUndoChange = false,
  onUndoLastChange = null,
  handleAccept,
  handleReject,
  aiContextScope = 'survey',
  aiContextScopeOptions = { list: [] },
  onAiContextScopeChange = null
}) => {

  const classes = useStyles();

  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [scopeMenuAnchor, setScopeMenuAnchor] = useState(null);
  const chatEndRef = useRef(null);
  const messageHistoryRef = useRef([]);
  const turnHistoryRef = useRef([]);
  const generationStatusRef = useRef({ isActive: false });
  const abortControllerRef = useRef(null);

  const handleOpenScopeMenu = event => {
    setScopeMenuAnchor(event.currentTarget);
  };

  const handleCloseScopeMenu = () => {
    setScopeMenuAnchor(null);
  };

  const handleScopeSelect = (scopeValue) => {
    if (onAiContextScopeChange) {
      onAiContextScopeChange({ target: { value: scopeValue } });
    }
    handleCloseScopeMenu();
  };

  const useFullSchema = getEnvVar("ADM_SURVEY_FULL_SCHEMA") === 'true';
  let schemaPayload = JSON.stringify(schema);
  if (useFullSchema) {
    try {
      schemaPayload = JSON.stringify(Serializer.generateSchema());
    } catch (e) {
      console.warn("Failed to generate full survey schema, falling back to simplified schema.", e);
    }
  }

  const promptMessage = getEnvVar("ADM_SURVEY_AI_PROMPT") || "You are a SurveyJS designer assistant for Agilysys Digital Marketing (ADM). Return ONLY a single, raw, strictly valid JSON object. NO markdown tags (e.g., ```json), NO prose, NO explanation, and NO commentary before or after the JSON. If you include any non-JSON text, the system will fail. RULES: 1. Root structure: {\"comment\": \"summary\", \"survey\": {schema}}. 2. VALID JSON VALUES: All values must be valid JSON types. EVERYTHING must be quoted if it is a string. NEVER leave placeholders unquoted (e.g., NEVER use \"imageLink\":<URL> or \"value\":<DATE_TIME>). Use valid strings like \"imageLink\": \"{{URL}}\" or \"imageLink\": null. 3. STRICT Name-Title Equality: Objects with \"name\" and \"title\" must have identical string values. 4. Referential Integrity: Sync all name changes in visibleIf, expressions, triggers, etc. 5. LITERAL OPERATORS: Use raw characters (<, >, <=, >=, =) inside strings; NEVER use HTML entities like &lt;. 6. BEHAVIORAL: Handle only survey requests; preserve logic unless reset is asked. FINAL CHECK: Output MUST be parseable by JSON.parse().";
  const schemaMessage = `Your generated JSON MUST correspond to this schema: ${schemaPayload}. Always check this schema. Do not add properties to your JSON if they do not exist in this schema. IMPORTANT: Even when providing the schema, DO NOT include markdown code blocks or any other formatting in your response. Return ONLY the raw JSON.`;
  const MAX_DYNAMIC_TRANSCRIPT_CHARS = 22000;
  const MAX_DYNAMIC_HISTORY_MESSAGES = parseInt(getEnvVar("ADM_SURVEY_CHAT_TURNS"), 10) || 12;

  const extractUsage = (usage = {}) => {
    const inputTokens = usage.prompt_tokens ?? usage.input_tokens ?? 0;
    const outputTokens = usage.completion_tokens ?? usage.output_tokens ?? 0;
    const totalTokens = usage.total_tokens ?? inputTokens + outputTokens;

    return {
      inputTokens,
      outputTokens,
      totalTokens
    };
  };

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);

  const initializeMessageHistory = () => {
    if (messageHistoryRef.current.length > 0) {
      return;
    }

    messageHistoryRef.current = [
      {
        "role": "system",
        "content": `${promptMessage} ${schemaMessage}`
      },
    ];
  };

  const buildQueryFromMessageHistory = messageHistory => {
    const seedMessages = messageHistory.slice(0, 3);
    const dynamicMessages = messageHistory.slice(3);
    const boundedDynamicMessages = dynamicMessages.slice(
      -MAX_DYNAMIC_HISTORY_MESSAGES * 2
    );
    const seedTranscript = seedMessages
      .map(message => `${message.role}: ${message.content}`)
      .join('\n');

    const reversedDynamicTranscriptParts = [];
    let currentDynamicChars = 0;
    for (let index = boundedDynamicMessages.length - 1; index >= 0; index -= 1) {
      const part = `${boundedDynamicMessages[index].role}: ${boundedDynamicMessages[index].content}`;
      const partLengthWithSeparator = part.length + 1;
      if (currentDynamicChars + partLengthWithSeparator > MAX_DYNAMIC_TRANSCRIPT_CHARS) {
        break;
      }
      reversedDynamicTranscriptParts.push(part);
      currentDynamicChars += partLengthWithSeparator;
    }

    const dynamicTranscript = reversedDynamicTranscriptParts.reverse().join('\n');
    return dynamicTranscript
      ? `${seedTranscript}\n${dynamicTranscript}`
      : seedTranscript;
  };

  const prepareSurveyJsGuidelineMessage = userMessage => {
    initializeMessageHistory();

    // Keep only the latest survey snapshot message for the current turn.
    messageHistoryRef.current = messageHistoryRef.current.filter(message => {
      if (message?.role !== 'user') {
        return true;
      }
      return !String(message?.content || '').startsWith('Current survey JSON: ');
    });

    const turnStartIndex = messageHistoryRef.current.length;

    const currentJsonText =
      typeof contextData === 'string'
        ? contextData
        : JSON.stringify(contextData || {});

    messageHistoryRef.current.push({
      role: 'user',
      content: `Current survey JSON: ${currentJsonText}`
    });

    const scopedUserMessage = selectedElement
      ? `Currently selected element: ${
          typeof selectedElement === 'string'
            ? selectedElement
            : selectedElement?.name || JSON.stringify(selectedElement)
        }. Apply changes only to this element. User request: ${userMessage}`
      : userMessage;

    messageHistoryRef.current.push({
      role: 'user',
      content: scopedUserMessage,
      text: userMessage
    });

    turnHistoryRef.current.push({
      prompt: userMessage,
      historyEntriesAdded: messageHistoryRef.current.length - turnStartIndex,
      scope: aiContextScope
    });

    const seedMessages = messageHistoryRef.current.slice(0, 3);
    const dynamicMessages = messageHistoryRef.current.slice(3);
    const boundedDynamicMessages = dynamicMessages.slice(
      -MAX_DYNAMIC_HISTORY_MESSAGES * 2
    );
    const boundedMessageHistory = [...seedMessages, ...boundedDynamicMessages];

    return {
      text: userMessage,
      messageHistory: boundedMessageHistory,
      query: buildQueryFromMessageHistory(messageHistoryRef.current)
    };
  };

  const getUsageHoverText = usage => {
    if (!usage) {
      return '';
    }
    return `Tokens: ${usage.totalTokens} (in: ${usage.inputTokens}, out: ${usage.outputTokens})`;
  };

  const removeLastChatTurn = () => {
    setChatMessages(prev => {
      if (prev.length === 0) {
        return prev;
      }
      let lastUserIndex = -1;
      for (let index = prev.length - 1; index >= 0; index -= 1) {
        if (prev[index]?.role === 'user') {
          lastUserIndex = index;
          break;
        }
      }
      if (lastUserIndex < 0) {
        return prev;
      }
      return prev.slice(0, lastUserIndex);
    });
  };

  const removeLastPromptTurn = () => {
    const lastTurn = turnHistoryRef.current.pop();
    if (!lastTurn?.historyEntriesAdded) {
      return;
    }
    const retainedLength = Math.max(
      0,
      messageHistoryRef.current.length - lastTurn.historyEntriesAdded
    );
    messageHistoryRef.current = messageHistoryRef.current.slice(0, retainedLength);
  };

  const handleUndoTurn = () => {
    if (isGenerating || turnHistoryRef.current.length === 0) {
      return;
    }
    removeLastChatTurn();
    removeLastPromptTurn();

    // After popping, turnHistoryRef now contains the history up to the previous turn.
    // Revert the UI scope to whatever was active for that previous turn,
    // or back to 'survey' if no turns remain.
    const lastTurn = turnHistoryRef.current[turnHistoryRef.current.length - 1];
    const targetScope = lastTurn?.scope || 'survey';

    if (typeof onUndoLastChange === 'function') {
      onUndoLastChange(targetScope);
    }
  };

  const handleStop = () => {
    generationStatusRef.current.isActive = false;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsGenerating(false);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    generationStatusRef.current.isActive = true;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const currentPrompt = prompt;
    setPrompt('');

    const currentScope = aiContextScope;
    const currentScopeDisplayText =
      aiContextScopeOptions?.list?.find(opt => opt.value === currentScope)?.displayText ||
      currentScope;

    setChatMessages(prev => [
      ...prev,
      {
        role: 'user',
        content: currentPrompt,
        scope: currentScope,
        scopeDisplayText: currentScopeDisplayText
      }
    ]);

    try {
      if (typeof requestAi !== 'function') {
        throw new Error('AI request handler is not configured');
      }

      const preparedMessage = prepareSurveyJsGuidelineMessage(currentPrompt);

      const result = await requestAi({
        text: preparedMessage.text,
        messageHistory: preparedMessage.messageHistory,
        query: preparedMessage.query,
        currentSurveyJSON: contextData,
        selectedElement,
        signal: abortController.signal
      });

      if (!generationStatusRef.current.isActive) {
        return;
      }

      const usage = extractUsage(result.usage);

      const responseComment = result?.comment || result?.response?.comment || '';
      const responseSurveyJson = result?.surveyJson || result?.survey || result?.response?.survey;
      const responseText = (typeof result?.response === 'string' ? result.response : '') || responseComment;

      // Persist assistant response into message history for next turn context
      if (responseComment || responseText) {
        messageHistoryRef.current.push({
          role: 'assistant',
          content: responseComment || responseText || ''
        });
      }

      let processedContent = responseText;
      let assistantMessage = responseComment;

      if (parseJson) {
        processedContent = responseSurveyJson;
        assistantMessage = responseComment || 'Updated successfully!';
      }
      setChatMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: parseJson ? assistantMessage : responseText,
          usage
        }
      ]);
      onSuccess(processedContent, {
        comment: assistantMessage,
        usage,
        model: result?.model || null
      });
    } catch (err) {
      if (!generationStatusRef.current.isActive) {
        return;
      }
      console.error('AI Generation error:', err);

      const isQuotaExceeded =
        err?.status === 429 ||
        err?.response?.status === 429;
      const errorMessage = isQuotaExceeded
        ? getEnvVar("ADM_SURVEY_AI_TOKEN_LIMIT_MESSAGE") || 'You have exceeded your AI token usage limit. Please try again later.'
        : getEnvVar("ADM_SURVEY_AI_ERROR_MESSAGE") || 'An error occurred while generating the AI response. Please try again.';

      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: errorMessage, isError: true }
      ]);
      if (onError) onError(err);
    } finally {
      setIsGenerating(false);
      generationStatusRef.current.isActive = false;
      abortControllerRef.current = null;
    }
  };

  const handleEditMessage = (messageContent, messageScope) => {
    if (!messageContent || isGenerating || turnHistoryRef.current.length === 0) {
      return;
    }
    removeLastChatTurn();
    removeLastPromptTurn();

    // Revert the UI scope to EXACTLY what was active for the turn we're editing back into.
    // This allows the user to redo the request with the same context scope pre-selected.
    const targetScope = messageScope || 'survey';

    if (typeof onUndoLastChange === 'function') {
      onUndoLastChange(targetScope);
    }
    setPrompt(messageContent);
  };

  const lastUserMessageIndex = (() => {
    for (let index = chatMessages.length - 1; index >= 0; index -= 1) {
      if (chatMessages[index]?.role === 'user') {
        return index;
      }
    }
    return -1;
  })();

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" style={{ boxShadow: '0px 1px 4px 0 rgba(0,0,0,0.1)', padding: '4px 8px', zIndex: 1, backgroundColor: '#ffffff' }}>
        <Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Box className={classes.avatarBox}>
              <img 
                src={`${process.env.PUBLIC_URL}/images/avatar_24px.svg`} 
                alt="AI Assistant" 
                style={{ width: 24, height: 24, margin: 8, verticalAlign: 'middle' }} />
            </Box>
            <Box>
              <Typography variant="h6" style={{ wordBreak: 'break-word' }}>{'AI Assistant'}</Typography>
              <Typography variant="body2" color="textSecondary" style={{ wordBreak: 'break-word' }}>{'Your digital companion'}</Typography>
            </Box>
          </Box>
        </Box>
        <CloseIcon onClickClose={handleReject} style={{ paddingTop: 0, paddingRight: 0 }} />
      </Box>
      <Box flexGrow={1} p={2} style={{ overflowY: 'auto' }}>
        <List>
          {[{ role: 'assistant', isWelcome: true },...chatMessages].map((msg, index) => (
            <ListItem
              key={index}
              alignItems="flex-start"
              style={{
                flexDirection: 'column',
                paddingLeft: 0,
                paddingRight: 0
              }}>
              <Box
                title={msg.usage ? getUsageHoverText(msg.usage) : ''}
                style={{
                  padding: '8px 12px',
                  maxWidth: '90%',
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  backgroundColor: msg.role === 'user' ? '#f0f4fe' : '#ffffff',
                  border: msg.isError ? '1px solid #e53935' : (msg.role === 'user' ? '0px solid #d0d0d0' : '0px solid #e0e0e0'),
                  borderRadius: '6px'
                }}>
                {msg.role === 'assistant' && (
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Box className={classes.avatarBox}>
                      <img 
                        src={`${process.env.PUBLIC_URL}/images/avatar_24px.svg`} 
                        alt="AI Assistant" 
                        style={{ width: 24, height: 24, margin: 4, verticalAlign: 'middle' }} />
                    </Box>
                    <Typography variant="body2" color={msg.isError ? 'error' : 'textSecondary'} style={{ wordBreak: 'break-word' }}>
                      {msg.isError ? 'System Error' : 'AI Assistant'}
                    </Typography>
                  </Box>
                )}
                {msg.isWelcome ? (
                  <Box>
                    <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 4 }}>Welcome to AI Assistant</Typography>
                    <Typography>I can help with creating or editing surveys.</Typography>
                  </Box>
                ) : (
                  <>
                    {msg.role === 'user' && msg.scopeDisplayText && (
                      <Box mb={1} display="flex" alignItems="center">
                        <Typography className={classes.aiScopeLabel} style={{ margin: 0, fontSize: '0.65rem' }}>
                          {msg.scopeDisplayText}
                        </Typography>
                      </Box>
                    )}
                    <Typography color={msg.isError ? 'error' : 'initial'} style={{ wordBreak: 'break-word' }}>{msg.content}</Typography>
                  </>
                )}
                {msg.role === 'user' && index === lastUserMessageIndex + 1 && (
                  <Box display="flex" justifyContent="flex-end" mt={0.5}>
                    <IconButton
                      size="small"
                      style={{ padding: 2 }}
                      title="Edit and resend"
                      disabled={isGenerating}
                      onClick={() => handleEditMessage(msg.content, msg.scope)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </ListItem>
          ))}
          {isGenerating && (
            <ListItem
              alignItems="flex-start"
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                paddingLeft: 12,
                paddingRight: 0
              }}>
              <Box className={classes.avatarBox}
              style={{
                display: 'flex',
                }}>
                <img 
                  src={`${process.env.PUBLIC_URL}/images/avatar_24px.svg`} 
                  alt="AI Assistant" 
                  style={{ width: 24, height: 24, margin: 4, verticalAlign: 'middle' }} />
              </Box>
              <div className={classes.typingLoader}>
                <span />
                <span />
                <span />
              </div>
            </ListItem>
          )}
          <div ref={chatEndRef} />
        </List>
      </Box>
      <Box className={classes.aiChatBox}>
        <Paper
          component="form"
          className={classes.aiChatInputPaper}
          onSubmit={e => {
            e.preventDefault();
            handleGenerate();
          }}>
          <Box style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                {aiContextScope && (
                  <Box display="flex">
                    <Typography
                      className={classes.aiScopeLabel}
                      style={{ cursor: isGenerating ? 'default' : 'pointer' }}
                      onClick={!(isGenerating || !aiContextScopeOptions || !aiContextScopeOptions.list) ? handleOpenScopeMenu : undefined}
                    >
                      {aiContextScopeOptions?.list?.find(opt => opt.value === aiContextScope)?.displayText || aiContextScope}
                    </Typography>
                  </Box>
                )}
              </div>
            </Box>
            <Box width="100%" display="flex" flexDirection="row" alignItems="center">
              <IconButton
                color="default"
                style={{ padding: 10 }}
                onClick={handleOpenScopeMenu}
                title="Select AI context scope"
                disabled={isGenerating || !aiContextScopeOptions || !aiContextScopeOptions.list}>
                <Add />
              </IconButton>
              <Menu
                id="scope-menu"
                anchorEl={scopeMenuAnchor}
                keepMounted
                open={Boolean(scopeMenuAnchor)}
                onClose={handleCloseScopeMenu}
              >
                {aiContextScopeOptions && aiContextScopeOptions.list && aiContextScopeOptions.list.map((option) => (
                  <MenuItem
                    key={option.id}
                    selected={option.value === aiContextScope}
                    disabled={option.isDisabled}
                    onClick={() => handleScopeSelect(option.value)}
                    style={option.customStyles}
                  >
                    <ListItemText primary={option.displayText} />
                  </MenuItem>
                ))}
              </Menu>
              <InputBase
                style={{ flex: 1, marginLeft: 0 }}
                placeholder="Ask me anything"
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                multiline
                className={classes.aiChatInput}
                maxRows={4}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleGenerate();
                  }
                }}
                disabled={isGenerating}
              />
              <IconButton
                color="default"
                style={{ padding: 10 }}
                onClick={handleUndoTurn}
                title="Undo last AI change"
                disabled={isGenerating || !canUndoChange || turnHistoryRef.current.length === 0}>
                <UndoIcon />
              </IconButton>
              {isGenerating ? (
                <IconButton
                  color="secondary"
                  className={classes.aiStopButton}
                  onClick={handleStop}
                  title="Stop generating">
                  <StopIcon style={{ borderRadius: '2px', background: 'black', fontSize: '14px' }} />
                </IconButton>
              ) : (
                <IconButton
                  color="primary"
                  className={classes.aiSendButton}
                  onClick={handleGenerate}
                  title="Generate"
                  disabled={!isGenerating && !prompt.trim()}>
                    <ArrowUpward style={{ fontSize: '20px' }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
      <Box className={classes.aiButtonBox}>
        <Button onClick={handleAccept} color="primary" variant="contained" disabled={isGenerating}>
          Apply
        </Button>
      </Box>
    </Box>
  );
};

SurveyAIGenerator.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func,
  parseJson: PropTypes.bool,
  contextData: PropTypes.any,
  selectedElement: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  requestAi: PropTypes.func,
  canUndoChange: PropTypes.bool,
  onUndoLastChange: PropTypes.func,
  handleAccept: PropTypes.func,
  handleReject: PropTypes.func,
  aiContextScope: PropTypes.string,
  aiContextScopeOptions: PropTypes.object,
  onAiContextScopeChange: PropTypes.func
};

export default SurveyAIGenerator;
